import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshop';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import {
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
  GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import { baseCell, booleanCell, adminCell } from 'components/cells';
import DataGridCell from 'components/cells/DataGridCell';
import {
  numberFilterOperators,
  filterOperators,
  dateFilterOperators,
  booleanFilterOperators,
  singleSelectFilterOperators
} from 'components/enhanced';
import { getStatusKey, toDateValue } from 'components/helpers/dateUtils';
import dayjs from 'dayjs';
import { ProductWarehouseStatus } from 'features/inventory/products/constants';
import { IStockQuantity } from 'features/inventory/stock/types';
import { printLabels } from 'features/zebra';
import { useTranslation } from 'react-i18next';
import { IsSuperAdminGuard } from 'shared/permissions/IsSuperAdmin';
import { StatusOption } from 'shared/types/general';

const getContractLines = (params) => {
  return params.row.contract_lines;
};

const getSupplierNames = (value: unknown): string[] => {
  const list = Array.isArray(value) ? value : [];

  return list.reduce<string[]>((suppliers, item) => {
    const supplier = typeof item?.supplier === 'string' ? item.supplier.trim() : '';

    if (!supplier || suppliers.includes(supplier)) {
      return suppliers;
    }

    suppliers.push(supplier);
    return suppliers;
  }, []);
};

export const StockQuantitiesColumns = (isAdmin = false, locked?: boolean): GridColDef[] => {
  const translationPrefix = 'entities.stock_quantity';
  const isSuperAdmin = IsSuperAdminGuard();
  const statusOptions: StatusOption[] = Object.entries(ProductWarehouseStatus).map(
    ([display_name, key]) => {
      return {
        value: Number(key),
        display_name: String(display_name)
      };
    }
  );
  const { t } = useTranslation();
  return [
    {
      field: 'actions',
      type: 'actions',
      headerName: t(`global.actions`),
      getActions: (params: GridRowParams) => {
        return [
          {
            label: t('actions.print_label'),
            icon: <LocalPrintshopIcon />,
            onClick: () =>
              printLabels(
                params.row['product'],
                params.row['product_batch'],
                params.row['expiration_date'],
                params.row['quantity'],
                params.row['description']
              )
          },
          {
            label: t('actions.print_barcode'),
            icon: <UnfoldLessIcon />,
            onClick: () =>
              printLabels(
                params.row['product'],
                params.row['product_batch'],
                params.row['expiration_date'],
                params.row['quantity'],
                params.row['description'],
                true
              )
          }
        ];
      },
      filterable: false
    } as GridColDef,
    {
      field: 'quantity',
      headerName: t(`${translationPrefix}.quantity`),
      filterOperators: numberFilterOperators,
      type: 'number',
      filterable: true,
      width: 70,
      sortable: true,
      editable: isAdmin,
      renderCell: (params: GridRenderCellParams<any>) => {
        const movement = params.row as IStockQuantity;
        return <>{movement.quantity}</>;
      }
    },
    {
      field: 'product',
      headerName: t(`global.product`),
      width: 180,
      filterOperators,
      sortable: !locked,
      filterable: !locked,
      renderCell: baseCell
    },
    {
      field: 'product__type',
      headerName: t(`global.type`),
      width: 180,
      filterOperators,
      sortable: !locked,
      filterable: !locked,
      renderCell: baseCell
    },
    {
      field: 'description',
      headerName: t(`global.description`),
      width: 180,
      filterOperators,
      sortable: !locked,
      filterable: !locked,
      renderCell: baseCell
    },
    {
      field: 'manufacturer_name',
      headerName: t(`entities.product.manufacturer_name`),
      width: 200,
      filterOperators,
      sortable: !locked,
      filterable: !locked,
      renderCell: baseCell
    },
    {
      field: 'product_type',
      headerName: t(`global.product_type`),
      width: 130,
      filterOperators,
      sortable: !locked,
      filterable: !locked,
      renderCell: baseCell
    },
    {
      field: 'manufacturer_id',
      headerName: t(`entities.product.manufacturer_id`),
      width: 140,
      filterOperators,
      sortable: !locked,
      filterable: !locked,
      renderCell: baseCell
    },
    {
      field: 'status',
      headerName: t(`global.status`),
      width: 80,
      type: 'singleSelect',
      valueOptions: statusOptions.map((opt) => {
        return {
          value: String(opt.value),
          label: t(`entities.product.status_options.${opt.value}`)
        };
      }),
      filterable: !locked,
      sortable: true,
      filterOperators: singleSelectFilterOperators,
      valueFormatter: (params) => {
        const statusKey = getStatusKey(params.value);
        return statusKey ? t(`entities.product.status_options.${statusKey}`) : '';
      },
      renderCell: ({ value }) => {
        const statusKey = getStatusKey(value);
        if (!statusKey) return <span />;

        return (
          <span className={statusKey === 'ACTIVE' ? 'text-green-500' : 'text-orange-500'}>
            {t(`entities.product.status_options.${statusKey}`)}
          </span>
        );
      }
    },
    {
      field: 'notes',
      headerName: t(`global.notes`),
      width: 180,
      filterOperators,
      filterable: !locked,
      renderCell: baseCell
    },
    {
      field: 'order_notes',
      headerName: t(`entities.product.order_notes`),
      width: 180,
      filterOperators,
      sortable: !locked,
      filterable: !locked,
      renderCell: baseCell
    },
    {
      field: 'category',
      headerName: t(`global.category`),
      width: 100,
      filterOperators,
      valueFormatter: (params: GridValueFormatterParams) => {
        return params.value;
      },
      renderCell: baseCell
    },
    {
      field: 'warehouse',
      headerName: t(`global.warehouse`),
      width: 200,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'stock_quantity_locations',
      headerName: t(`entities.stock_quantity.locations`),
      width: 180,
      filterable: false,
      sortable: false,
      valueGetter: (params) => {
        const sqLocs = params.row?.stock_quantity_locations;
        return Array.isArray(sqLocs) && sqLocs.length > 0 ? sqLocs.join(', ') : '';
      },
      renderCell: (params: GridRenderCellParams) => {
        const sqLocs = params.row?.stock_quantity_locations;
        const text = Array.isArray(sqLocs) && sqLocs.length > 0 ? sqLocs.join(', ') : '';
        return <>{text}</>;
      }
    },
    {
      field: 'suppliers',
      valueGetter: getContractLines,
      headerName: t(`${translationPrefix}.suppliers`),
      renderCell: (params: GridRenderCellParams) => {
        const suppliers = getSupplierNames(params.value);
        return <DataGridCell content={suppliers.join(', ')} />;
      },
      valueFormatter: (params) => {
        const raw =
          params != null && typeof params === 'object' && 'value' in params ? params.value : params;
        const suppliers = getSupplierNames(raw);
        return suppliers.join(', ');
      },
      filterOperators,
      sortable: !locked,
      filterable: !locked,
      width: 300
    },
    {
      field: 'product_batch',
      headerName: t(`${translationPrefix}.product_batch`),
      editable: false,
      width: 100,
      filterOperators,
      renderCell: baseCell
    },
    {
      field: 'expiration_date',
      headerName: t(`${translationPrefix}.expiration_date`),
      filterOperators: dateFilterOperators,
      valueGetter: (params) => toDateValue(params.row?.expiration_date),
      valueFormatter: (params) => params.value && dayjs(params.value).format('DD/MM/YYYY'),
      editable: false,
      width: 100,
      type: 'date'
    },
    {
      field: 'discounted',
      headerName: t(`${translationPrefix}.discounted`),
      width: 60,
      filterOperators: booleanFilterOperators,
      valueFormatter: (params) => (params.value ? t('global.yes') : t('global.no')),
      renderCell: booleanCell(t)
    },
    {
      field: 'movement_unit_price',
      headerName: t(`${translationPrefix}.movement_unit_price`),
      width: 100,
      filterOperators: numberFilterOperators,
      filterable: true,
      valueGetter: (params) =>
        params.row?.movement_unit_price
          ? params.row.movement_unit_price
          : params.row.unit_price
          ? params.row.unit_price
          : '',
      sortable: true,
      renderCell: adminCell
    },
    {
      field: 'amount',
      headerName: t(`global.amount`),
      width: 100,
      type: 'number',
      filterOperators: numberFilterOperators,
      filterable: true,
      sortable: true,
      editable: isAdmin,
      renderCell: adminCell
    }
  ];
};
